import { useRouter } from '@tanstack/react-router';
import { mapApiErrorToForm } from '@upme/api-client';
import type {
  AdminPlaceDto,
  CityDto,
  PlaceCategoryDto,
  PlaceCreateInput,
  PlaceUpdateInput,
} from '@upme/contracts';
import { PlaceCreateInput as PCrSchema } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { useState } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { zodResolver } from '../../../app/zod-resolver';
import { useCreatePlace, useUpdatePlace } from '../api/places.mutations';
import { parseCoordsPaste } from '../lib/coords-paste';

type CreateProps = {
  mode: 'create';
  cities: ReadonlyArray<CityDto>;
  categories: ReadonlyArray<PlaceCategoryDto>;
  initialValues?: Partial<PlaceCreateInput> & { googlePlaceId?: string };
  importedCoverPreviewUrl?: string;
  importedCoverStorageKey?: string;
};
type EditProps = {
  mode: 'edit';
  cities: ReadonlyArray<CityDto>;
  categories: ReadonlyArray<PlaceCategoryDto>;
  place: AdminPlaceDto;
};
type Props = CreateProps | EditProps;

type FormValues = PlaceCreateInput & { isActive?: boolean };

export function PlaceForm(props: Props) {
  const router = useRouter({ warn: false });
  const isCreate = props.mode === 'create';

  const defaults: Partial<FormValues> = isCreate
    ? { categoryIds: [], ...(props.initialValues ?? {}) }
    : {
        cityId: props.place.cityId,
        name: props.place.name,
        slug: props.place.slug,
        description: props.place.description ?? undefined,
        lat: props.place.lat,
        lng: props.place.lng,
        addressText: props.place.addressText,
        websiteUrl: props.place.websiteUrl ?? undefined,
        instagramUsername: props.place.instagramUsername ?? undefined,
        managerTelegramUsername: props.place.managerTelegramUsername ?? undefined,
        openingHoursText: props.place.openingHoursText ?? undefined,
        coverImageUrl: props.place.coverImageUrl ?? undefined,
        categoryIds: props.place.categories.map((c) => c.id),
        isActive: props.place.isActive,
      };

  const form = useForm<FormValues>({
    resolver: zodResolver(PCrSchema as unknown as z.ZodType<FormValues>) as Resolver<FormValues>,
    defaultValues: defaults as FormValues,
    mode: 'onSubmit',
  });

  const [coordsPasted, setCoordsPasted] = useState(false);

  const create = useCreatePlace();
  const update = useUpdatePlace(isCreate ? '' : props.place.id);

  const cityId = form.watch('cityId');
  const categoryIds = form.watch('categoryIds') ?? [];
  // Выключенные категории сервер отклоняет 422 ('one or more categories are inactive'),
  // поэтому выбрать их нельзя. Уже назначенные месту оставляем в списке — иначе их
  // не снять, а сохранение падало бы на той же 422.
  const selectableCategories = props.categories.filter((c) => c.isActive || categoryIds.includes(c.id));
  const lat = form.watch('lat');
  const lng = form.watch('lng');

  function handleCoordsPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    const parsed = parseCoordsPaste(text);
    if (parsed) {
      e.preventDefault();
      form.setValue('lat', parsed.lat);
      form.setValue('lng', parsed.lng);
      setCoordsPasted(true);
    }
  }

  const onSubmit = form.handleSubmit(
    async (values) => {
      try {
        const payload: PlaceCreateInput = {
          ...values,
          description: values.description || undefined,
          websiteUrl: values.websiteUrl || undefined,
          instagramUsername: values.instagramUsername || undefined,
          managerTelegramUsername: values.managerTelegramUsername || undefined,
          openingHoursText: values.openingHoursText || undefined,
          coverImageUrl: values.coverImageUrl || undefined,
          slug: values.slug || undefined,
        } as PlaceCreateInput;

        const submitBody: PlaceCreateInput = {
          ...payload,
          ...(isCreate && props.importedCoverStorageKey
            ? { coverStorageKey: props.importedCoverStorageKey }
            : {}),
          ...(isCreate && props.initialValues?.googlePlaceId
            ? { googlePlaceId: props.initialValues.googlePlaceId }
            : {}),
        };

        let result: AdminPlaceDto;
        if (isCreate) {
          result = await create.mutateAsync(submitBody);
        } else {
          const updatePayload: PlaceUpdateInput = {
            ...payload,
            isActive: values.isActive,
          };
          result = await update.mutateAsync(updatePayload);
        }
        toast.success(isCreate ? 'Место создано' : 'Изменения сохранены');
        router?.navigate({ to: '/places/$placeId', params: { placeId: result.id } } as never);
      } catch (err) {
        await mapApiErrorToForm(err, form);
      }
    },
    (errors) => {
      const first = Object.values(errors).find(
        (e): e is { message?: string } => typeof e === 'object' && e !== null && 'message' in e,
      );
      const msg = first?.message ? String(first.message) : 'Не все поля заполнены корректно';
      toast.error(`Проверьте форму: ${msg}`);
    },
  );

  const rootServer = (form.formState.errors.root as { server?: { message?: string } } | undefined)?.server;

  const err = (k: keyof FormValues) => {
    const e = form.formState.errors[k] as { message?: string } | undefined;
    return e?.message ? <p className="text-sm text-destructive mt-1">{String(e.message)}</p> : null;
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5 max-w-3xl" noValidate>
      {isCreate && props.importedCoverPreviewUrl ? (
        <div className="mb-4">
          <Label>Обложка</Label>
          <img
            src={props.importedCoverPreviewUrl}
            alt="обложка"
            className="mt-1 h-48 w-full rounded object-cover"
          />
        </div>
      ) : null}

      <div>
        <Label>Город</Label>
        <Select
          value={cityId ?? ''}
          onValueChange={(v) => form.setValue('cityId', v, { shouldValidate: true })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Выберите город" />
          </SelectTrigger>
          <SelectContent>
            {props.cities.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {err('cityId')}
      </div>

      <div>
        <Label htmlFor="name">Название</Label>
        <Input id="name" {...form.register('name')} placeholder="Кофейня «Берег»" />
        {err('name')}
      </div>

      <div>
        <Label htmlFor="slug">Slug (опционально)</Label>
        <Input id="slug" {...form.register('slug')} placeholder="auto-generated" />
        <p className="text-xs text-muted-foreground mt-1">
          Оставьте пустым — сгенерируется автоматически из названия.
        </p>
        {err('slug')}
      </div>

      <div>
        <Label htmlFor="description">Описание</Label>
        <textarea
          id="description"
          rows={3}
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
          {...form.register('description')}
        />
        {err('description')}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="lat">Lat</Label>
          <Input
            id="lat"
            type="number"
            step="0.000001"
            {...form.register('lat', { valueAsNumber: true })}
            onPaste={handleCoordsPaste}
          />
          {err('lat')}
        </div>
        <div>
          <Label htmlFor="lng">Lng</Label>
          <Input
            id="lng"
            type="number"
            step="0.000001"
            {...form.register('lng', { valueAsNumber: true })}
            onPaste={handleCoordsPaste}
          />
          {err('lng')}
        </div>
        <p className="col-span-2 text-xs text-muted-foreground">
          Подсказка: вставьте «lat,lng» из Google Maps — поля заполнятся автоматически.
        </p>
        {coordsPasted && (
          <a
            className="col-span-2 text-xs text-blue-500 underline"
            target="_blank"
            rel="noreferrer"
            href={`https://maps.google.com/?q=${lat},${lng}`}
          >
            Открыть в картах
          </a>
        )}
      </div>

      <div>
        <Label htmlFor="addressText">Адрес</Label>
        <Input id="addressText" {...form.register('addressText')} placeholder="ул. Бенх-Тань, 23" />
        {err('addressText')}
      </div>

      <div>
        <Label htmlFor="coverImageUrl">Cover (URL)</Label>
        <Input id="coverImageUrl" {...form.register('coverImageUrl')} placeholder="https://…" />
        {err('coverImageUrl')}
      </div>

      <div>
        <Label htmlFor="websiteUrl">Сайт</Label>
        <Input id="websiteUrl" {...form.register('websiteUrl')} placeholder="https://…" />
        {err('websiteUrl')}
      </div>

      <div>
        <Label htmlFor="instagramUsername">Instagram username (без @)</Label>
        <Input id="instagramUsername" {...form.register('instagramUsername')} placeholder="cafe_hcmc" />
        {err('instagramUsername')}
      </div>

      <div>
        <Label htmlFor="managerTelegramUsername">Telegram менеджера (без @)</Label>
        <Input
          id="managerTelegramUsername"
          {...form.register('managerTelegramUsername')}
          placeholder="username"
        />
        {err('managerTelegramUsername')}
      </div>

      <div>
        <Label htmlFor="openingHoursText">Часы работы</Label>
        <textarea
          id="openingHoursText"
          rows={2}
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
          placeholder="Mon-Fri 9-21, Sat-Sun 10-22"
          {...form.register('openingHoursText')}
        />
        {err('openingHoursText')}
      </div>

      <div>
        <Label>Категории</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {selectableCategories.map((c) => {
            const checked = categoryIds.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                  checked
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground hover:bg-muted'
                }`}
                onClick={() => {
                  const ids = form.getValues('categoryIds');
                  form.setValue('categoryIds', checked ? ids.filter((id) => id !== c.id) : [...ids, c.id], {
                    shouldValidate: true,
                  });
                }}
              >
                {c.icon ? `${c.icon} ` : ''}
                {c.name}
              </button>
            );
          })}
        </div>
        {err('categoryIds')}
      </div>

      {!isCreate && (
        <div className="flex items-center gap-2">
          <input
            id="isActive"
            type="checkbox"
            className="h-4 w-4"
            checked={form.watch('isActive') ?? props.place.isActive}
            onChange={(e) => form.setValue('isActive', e.currentTarget.checked)}
          />
          <Label htmlFor="isActive">Активно</Label>
        </div>
      )}

      {rootServer?.message ? (
        <p className="text-sm text-destructive" role="alert">
          {rootServer.message}
        </p>
      ) : null}

      <Button type="submit" disabled={form.formState.isSubmitting}>
        {isCreate ? 'Создать' : 'Сохранить'}
      </Button>
    </form>
  );
}
